import { skipToken, useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { memo } from 'react'

import { Chip, useTranslate } from '@orus.eu/pharaoh'
import type { OrganizationFunnelInfo } from '@orus.eu/right-access-management'

import { useTRPC } from '../../../../client.ts'
import {
  resolveSubscriptionBrokerClientArgs,
  useIsReferralFeatureAvailable,
} from '../../../../lib/hooks/use-is-referral-feature-available.ts'
import type { CommonSubscriptionProps } from '../subscription-v2-props.ts'

type ReferrerBadgeProps = {
  referralCodeUsed?: string | undefined
  organization?: OrganizationFunnelInfo | undefined
  context?: CommonSubscriptionProps['context'] | undefined
  customerId?: string | undefined
}

export const ReferrerBadge = memo<ReferrerBadgeProps>(function ReferrerBadge({
  referralCodeUsed,
  organization,
  context,
  customerId,
}) {
  const trpc = useTRPC()
  const translate = useTranslate()
  const { isReferralFeatureAvailable, isPending } = useIsReferralFeatureAvailable(
    organization,
    resolveSubscriptionBrokerClientArgs({ context: context ?? 'selfonboarding', customerId }),
  )
  const { referral_code: referralCodeSearchParam } = useSearch({ strict: false })

  // The dimension is authoritative once subscription state exists: 'none' means no valid referral
  // (never set, cleared as self-referral, or overridden by a discount) and must not fall back to
  // the URL param. The fallback only covers the landing page, before any subscription state exists.
  const referralCode =
    referralCodeUsed === undefined
      ? referralCodeSearchParam
      : referralCodeUsed !== 'none' && referralCodeUsed !== ''
        ? referralCodeUsed
        : undefined

  const { data: firstName } = useQuery(
    trpc.referral.getUserFirstNameByReferralCode.queryOptions(referralCode ? { referralCode } : skipToken),
  )

  if (isPending || !isReferralFeatureAvailable) return null
  if (!firstName) return null

  return (
    <Chip variant="brand" size="small" icon="gift-regular">
      {translate('referred_by', { firstName })}
    </Chip>
  )
})
